import { Search } from "lucide-react";
import { useDepartmentOptions, useEmployeeOptions } from "#/features/employees/hooks";
import type { AdminDocumentFilters, MyDocumentFilters } from "../types";

export const CURRENT_YEAR = new Date().getFullYear();
// Last 6 years — covers most HR document lookback (payslips, contracts, certs).
// "All years" is handled by the separate checkbox next to the select, not an option here.
export const YEAR_OPTS = Array.from({ length: 6 }, (_, i) => {
  const y = CURRENT_YEAR - i;
  return { value: String(y), label: String(y) };
});

/** True if the caller has applied any filter beyond the default (current-year) view. */
export function hasCustomFilters(filters: Record<string, unknown>): boolean {
  return Object.entries(filters).some(([key, value]) => {
    if (value === undefined || value === null || value === "") return false;
    if (key === "year" && value === CURRENT_YEAR) return false; // the default, not a user override
    return true;
  });
}

export const VISIBILITY_OPTS = [
  { value: "",             label: "All visibilities" },
  { value: "INTERNAL",     label: "Internal" },
  { value: "RESTRICTED",   label: "Restricted" },
  { value: "CONFIDENTIAL", label: "Confidential" },
];

export const STATUS_OPTS = [
  { value: "ACTIVE",  label: "Active" },
  { value: "DELETED", label: "Deleted (recovery/audit)" },
];

export const DOC_TYPE_OPTS = [
  { value: "", label: "All types" },
  { value: "CONTRACT",         label: "Contract" },
  { value: "ID",               label: "ID / Passport" },
  { value: "CERTIFICATE",      label: "Certificate" },
  { value: "PAYSLIP",          label: "Payslip" },
  { value: "LEAVE_ATTACHMENT", label: "Leave Attachment" },
  { value: "CLAIM_RECEIPT",    label: "Claim Receipt" },
  { value: "TRAINING",         label: "Training" },
  { value: "OTHER",            label: "Other" },
];

// Keep in sync with DocumentModuleCode enum in types.ts
export const MODULE_OPTS = [
  { value: "",            label: "All modules" },
  { value: "LEAVE",       label: "Leave" },
  { value: "CLAIM",       label: "Claims" },
  { value: "REQUISITION", label: "Requisitions" },
  { value: "TRAINING",    label: "Training" },
  { value: "INVENTORY",   label: "Inventory" },
];

// ── Admin filters (all documents) ─────────────────────────────────────────────

type AdminProps = {
  filters: AdminDocumentFilters;
  onChange: (f: AdminDocumentFilters) => void;
};

export function AdminDocumentFiltersBar({ filters, onChange }: AdminProps) {
  const { data: departments } = useDepartmentOptions();
  const { data: employees }   = useEmployeeOptions();

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative min-w-[200px] flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search by filename or employee…"
          value={filters.search ?? ""}
          onChange={(e) => onChange({ ...filters, search: e.target.value || undefined })}
          className="input pl-9"
        />
      </div>

      <select
        className="input w-auto min-w-[150px]"
        value={filters.documentType ?? ""}
        onChange={(e) => onChange({ ...filters, documentType: e.target.value || undefined })}
      >
        {DOC_TYPE_OPTS.map((t) => (
          <option key={t.value} value={t.value}>{t.label}</option>
        ))}
      </select>

      <select
        className="input w-auto min-w-[150px]"
        value={filters.moduleCode ?? ""}
        onChange={(e) => onChange({ ...filters, moduleCode: e.target.value || undefined })}
      >
        {MODULE_OPTS.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>

      <select
        className="input w-auto min-w-[160px]"
        value={filters.departmentId ? String(filters.departmentId) : ""}
        onChange={(e) =>
          onChange({ ...filters, departmentId: e.target.value ? Number(e.target.value) : undefined })
        }
      >
        <option value="">All departments</option>
        {departments?.map((d) => (
          <option key={d.id} value={d.id}>{d.name}</option>
        ))}
      </select>

      <select
        className="input w-auto min-w-[160px]"
        value={filters.employeeId ? String(filters.employeeId) : ""}
        onChange={(e) =>
          onChange({ ...filters, employeeId: e.target.value ? Number(e.target.value) : undefined })
        }
      >
        <option value="">All employees</option>
        {employees?.map((e) => (
          <option key={e.id} value={e.id}>{e.fullName}</option>
        ))}
      </select>

      <div className="flex items-center gap-2">
        <select
          className="input w-auto min-w-[100px] disabled:opacity-50"
          value={filters.year ? String(filters.year) : String(CURRENT_YEAR)}
          disabled={!filters.year}
          onChange={(e) => onChange({ ...filters, year: Number(e.target.value) })}
        >
          {YEAR_OPTS.map((y) => (
            <option key={y.value} value={y.value}>{y.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-slate-500">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded border-slate-300"
            checked={!filters.year}
            onChange={(e) => onChange({ ...filters, year: e.target.checked ? undefined : CURRENT_YEAR })}
          />
          All Years
        </label>
      </div>

      <select
        className="input w-auto min-w-[150px]"
        value={filters.visibility ?? ""}
        onChange={(e) => onChange({ ...filters, visibility: e.target.value || undefined })}
      >
        {VISIBILITY_OPTS.map((v) => (
          <option key={v.value} value={v.value}>{v.label}</option>
        ))}
      </select>

      <select
        className="input w-auto min-w-[170px]"
        value={filters.status ?? "ACTIVE"}
        onChange={(e) =>
          onChange({ ...filters, status: e.target.value === "ACTIVE" ? undefined : e.target.value })
        }
        title="Deleted documents are only visible to HR_DOCUMENT_MANAGE / SUPER_ADMIN"
      >
        {STATUS_OPTS.map((s) => (
          <option key={s.value} value={s.value}>{s.label}</option>
        ))}
      </select>
    </div>
  );
}

// ── My documents filters (self-service) ───────────────────────────────────────

type MyProps = {
  filters: MyDocumentFilters;
  onChange: (f: MyDocumentFilters) => void;
};

export function MyDocumentFiltersBar({ filters, onChange }: MyProps) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative min-w-[200px] flex-1">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <input
          type="text"
          placeholder="Search by filename…"
          value={filters.search ?? ""}
          onChange={(e) => onChange({ ...filters, search: e.target.value || undefined })}
          className="input pl-9"
        />
      </div>

      <select
        className="input w-auto min-w-[150px]"
        value={filters.moduleCode ?? ""}
        onChange={(e) => onChange({ ...filters, moduleCode: e.target.value || undefined })}
      >
        {MODULE_OPTS.map((m) => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>

      <div className="flex items-center gap-2">
        <select
          className="input w-auto min-w-[100px] disabled:opacity-50"
          value={filters.year ? String(filters.year) : String(CURRENT_YEAR)}
          disabled={!filters.year}
          onChange={(e) => onChange({ ...filters, year: Number(e.target.value) })}
        >
          {YEAR_OPTS.map((y) => (
            <option key={y.value} value={y.value}>{y.label}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 whitespace-nowrap text-xs font-medium text-slate-500">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded border-slate-300"
            checked={!filters.year}
            onChange={(e) => onChange({ ...filters, year: e.target.checked ? undefined : CURRENT_YEAR })}
          />
          All Years
        </label>
      </div>
    </div>
  );
}

// ── Legacy export (used by older code that imports DocumentFilters) ────────────
// Kept for backward compatibility — maps to AdminDocumentFiltersBar
export { AdminDocumentFiltersBar as DocumentFilters };
